import { Inbox } from 'lucide-react';
import { claimCrmLead, type CrmIntakeItem } from '../../../api';
import { useCrmIntake, useInvalidateCrm } from '../../../hooks/crm/useCrmQueries';
import { formatAdminPanelError } from '@/lib/adminApiError';
import { CrmInlineError } from './CrmInlineError';
import type { CrmAccessParams } from '../../../crmAccess';
import { canCrmFullControl } from '../../../crmAccess';
import { intakeSourceLabel } from './crmDealStrip';

type Props = {
  access: CrmAccessParams;
  managerId?: number | null;
  onOpenLead: (leadId: number) => void;
  hapticSelection: () => void;
  hapticSuccess: () => void;
};

export function CrmIntakeQueue({
  access,
  managerId,
  onOpenLead,
  hapticSelection,
  hapticSuccess,
}: Props) {
  const { data, isLoading, isError, error, refetch } = useCrmIntake(managerId);
  const invalidate = useInvalidateCrm();
  const canClaimAll = canCrmFullControl(access);

  const claim = async (item: CrmIntakeItem) => {
    hapticSelection();
    try {
      await claimCrmLead(item.id);
      hapticSuccess();
      void refetch();
      void invalidate();
      onOpenLead(item.id);
    } catch {
      /* toast handled by api */
    }
  };

  if (isLoading && !data) {
    return <p className="text-xs py-2" style={{ color: 'var(--text-3)' }}>Загрузка входящих…</p>;
  }
  if (isError) {
    return (
      <CrmInlineError
        message={formatAdminPanelError(error, 'Не удалось загрузить входящие')}
        onRetry={() => void refetch()}
      />
    );
  }
  if (!data?.items.length) {
    return (
      <p className="text-xs py-3 text-center" style={{ color: 'var(--text-3)' }}>
        Нет необработанных входящих
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {data.items.slice(0, 12).map((item) => (
        <div
          key={item.id}
          className="rounded-xl overflow-hidden"
          style={{ background: 'rgba(252,129,74,0.08)', border: '1px solid rgba(252,129,74,0.25)' }}
        >
          <button
            type="button"
            className="press w-full text-left p-3 touch-manipulation min-h-[44px]"
            onClick={() => {
              hapticSelection();
              onOpenLead(item.id);
            }}
          >
            <div className="flex items-center gap-2 mb-1">
              <span
                className="text-[9px] font-bold px-1.5 py-0.5 rounded shrink-0"
                style={{ background: 'rgba(99,179,237,0.15)', color: '#63B3ED' }}
              >
                {intakeSourceLabel(item.source)}
              </span>
              <p className="text-xs font-bold truncate flex-1" style={{ color: 'var(--text-1)' }}>
                {item.title}
              </p>
            </div>
            {item.last_message_preview && (
              <p className="text-[10px] line-clamp-2 leading-relaxed" style={{ color: 'var(--text-3)' }}>
                {item.last_message_preview}
              </p>
            )}
          </button>
          {(!item.assigned_manager_id || canClaimAll) && (
            <button
              type="button"
              onClick={() => void claim(item)}
              className="press w-full py-3 text-xs font-bold touch-manipulation min-h-[48px]"
              style={{
                background: 'rgba(99,179,237,0.2)',
                color: '#63B3ED',
                borderTop: '1px solid rgba(99,179,237,0.25)',
              }}
            >
              Взять в работу
            </button>
          )}
        </div>
      ))}
      {data.unassigned_count > data.items.length && (
        <p className="text-[10px] text-center flex items-center justify-center gap-1" style={{ color: 'var(--text-3)' }}>
          <Inbox size={12} /> ещё {data.unassigned_count - data.items.length} без менеджера
        </p>
      )}
    </div>
  );
}
